"use client";

import { DirectionProvider } from "@base-ui/react/direction-provider";
import { type ReactNode, use, useEffect, useMemo } from "react";
import { type AppLocale, DEFAULT_LOCALE, localeDirection } from "shared/Locale";
import { loadCatalog } from "src/i18n/catalogLoader";
import { DEFAULT_TIME_ZONE, FORMATS } from "src/i18n/config";
import { LocaleContext } from "src/i18n/LocaleContext";
import { IntlProvider } from "use-intl/react";

export interface PortalLocaleProps {
  locale: AppLocale;
  children: ReactNode;
}

/**
 * Translates everything inside it into `locale`: `useExtracted()` messages, use-intl formatting and
 * the document's `lang` and `dir` (also set on the server, in the root document). Suspends while the
 * locale's catalog loads. Wraps the whole document (src/routes/__root.tsx), so dialogs and overlays
 * outside the portal layout are translated too.
 */
const PortalLocale = ({ locale, children }: PortalLocaleProps) => {
  const catalog = locale === DEFAULT_LOCALE ? undefined : use(loadCatalog(locale));
  const value = useMemo(() => ({ locale, catalog }), [locale, catalog]);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = localeDirection(locale);
    return () => {
      root.lang = DEFAULT_LOCALE;
      root.dir = "ltr";
    };
  }, [locale]);

  return (
    <LocaleContext value={value}>
      <IntlProvider locale={locale} timeZone={DEFAULT_TIME_ZONE} formats={FORMATS}>
        {/* Base UI (menus, sliders, tabs, popup sides) reads the direction from context. */}
        <DirectionProvider direction={localeDirection(locale)}>{children}</DirectionProvider>
      </IntlProvider>
    </LocaleContext>
  );
};

export default PortalLocale;
